import { Box, CircularProgress } from "@mui/material";
import React from "react";
import {
  BodyLarge,
  BodySmall,
  LabelSmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import { StyledCheckbox } from "../../../components/StyledComponents/Inputs/Inputs";
import { useInView } from "react-intersection-observer";
import { StyledLabel } from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import { useHistory } from "react-router-dom";
import { StyledIconButton } from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from "../../../ThemeContext/ThemeContext.js";

// Reusable sub-component for rendering a single tag
const TagListItem = ({
  tag,
  hasBorder,
  handleCheckboxChange,
  attachTagsMode,
  detachTagsMode,
  viewMode,
}) => {
  const history = useHistory();
  const { colors } = useTheme();
  return (
    <>
      {viewMode ? (
        <Box
          display="flex"
          className="transaction-tag-list-item"
          justifyContent="space-between"
          alignItems="center"
          padding={"0.5rem 1.5rem 0.5rem 1rem"}
          borderBottom={hasBorder ? `1px solid ${colors?.border?.card}` : "none"}  
          sx={{
            cursor: "pointer",
            transition: "all 0.3s ease-in-out",
            backgroundColor: tag.isTagSelected ? colors?.hover?.secondary : "transparent",  
            borderRadius: tag.isTagSelected ? "6.25rem" : "0",
            "&:hover": {
              backgroundColor: colors?.hover?.muted,
              borderRadius: "6.25rem",
            }
          }}
        >
          <Box
            className="multi-select-tag-name-container"
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={2}
          >
            <BodyLarge
              noWrap
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: {
                  xs: "3rem",
                  sm: "4rem",
                  md: "8rem",
                  lg: "10rem",
                  xl: "12rem",
                }
              }}
            >
              {tag?.tag}
            </BodyLarge>
            {tag.isDefaultTag && !detachTagsMode && (
              <StyledLabel colors={colors}>
                <BodySmall>Default</BodySmall>
              </StyledLabel>
            )}
          </Box>
          {/* {!detachTagsMode && <LabelSmall>{tag?.occurrence}</LabelSmall>} */}
        </Box>
      ) : (
        <Box
          display="flex"
          className="transaction-tag-list-item"
          justifyContent="space-between"
          alignItems="center"
          padding={"0.5rem 1.5rem 0.5rem 1rem"}
          borderBottom={hasBorder ? `1px solid ${colors?.border?.card}` : "none"}  
          sx={{
            cursor: "pointer",
            transition: "all 0.3s ease-in-out",
            backgroundColor: tag.isTagSelected ? colors?.hover?.secondary : "transparent",  
            borderRadius: tag.isTagSelected ? "6.25rem" : "0",
            "&:hover": {
              backgroundColor: colors?.hover?.muted,
              borderRadius: "6.25rem",
            }
          }}
        >
          <Box
            className="multi-select-tag-name-container"
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={2}
          >
            <StyledCheckbox
              colors={colors}
              disabled={!attachTagsMode && tag.isDefaultTag}
              checked={tag.isTagSelected}
              onChange={(event) => handleCheckboxChange(event, tag)}
            />
            <BodyLarge
              noWrap
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: {
                  xs: "3rem",
                  sm: "4rem",
                  md: "8rem",
                  lg: "10rem",
                  xl: "12rem",
                }
              }}
            >
              {tag?.tag}
            </BodyLarge>
            {tag.isDefaultTag && !detachTagsMode && (
              <StyledLabel colors={colors}>
                <BodySmall>Default</BodySmall>
              </StyledLabel>
            )}
          </Box>
          {!detachTagsMode && (
            <Box 
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
              flexGrow={1}
              gap={{ xs: 1, sm: 2, md: 4 }}
              alignItems={"center"}
            >
              <LabelSmall sx={{ color: colors?.text?.muted }}>
                {tag?.occurrence}
              </LabelSmall>
              <StyledIconButton colors={colors}
                onClick={(event) => {
                  event.stopPropagation();
                  
                    if (typeof tag?.tag === "string" && tag.tag !== "") {
                      history.push(`/minerstatus?tags=${tag.tag}`);
                      try {
                        const storageToClear = [
                          "minerFilters",
                          "siteGroup"
                        ];
                        storageToClear.forEach((key) => {
                          sessionStorage.removeItem(key);
                        });
                        const newMinerFilters = {
                          tags: [tag.tag]
                        };
                        sessionStorage.setItem("minerFilters", JSON.stringify(newMinerFilters));
                      } catch (e) {
                        console.error("Failed to update sessionStorage:", e);
                      }
                    }
                  
                }}
              >
                <KeyboardArrowRightIcon />
              </StyledIconButton>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

const TagList = (props) => {
  const {
    tagList,
    isTagsDataLoading,
    isTagsDataFetched,
    maxHeight,
    hasNextPage,
    fetchNextPage,
    setTagData,
    attachTagsMode,
    detachTagsMode,
    viewMode,
    setTagList,
  } = props;
  const { colors } = useTheme();
  const { ref, inView } = useInView({
    threshold: 0.1,
    // triggerOnce: true,
  });

  React.useEffect(() => {
    if (inView && hasNextPage && fetchNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  // Simplified handler: only updates the tag's selection state.
  const handleCheckboxChange = (event, tag) => {
    const updatedTagList = tagList.map((item) => {
      if (item.tag === tag.tag) {
        return {
          ...item,
          isTagSelected: event.target.checked,
        };
      }
      return item;
    });
    setTagData(updatedTagList);
  };

  if (isTagsDataLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexGrow={1}
      >
        <CircularProgress
          size={"2rem"}
          sx={{
            color: colors.primary.main,
          }}
        />
      </Box>
    );
  }
  if (isTagsDataFetched && tagList.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexGrow={1}
      >
        <BodyLarge
          variant="h6"
          sx={{
            color: colors.primary.main,
          }}
        >
          No tags to show.
        </BodyLarge>
      </Box>
    );
  }

  if (
    isTagsDataFetched &&
    tagList &&
    Array.isArray(tagList) &&
    tagList.length > 0
  ) {
    return (
      <Box
        display="flex"
        flexDirection={"column"}
        flexGrow={1}
        overflow={"auto"}
        sx={{ scrollbarWidth: "thin" }}
        maxHeight={maxHeight ? maxHeight : "calc(100vh - 23rem)"}
      >
        {(detachTagsMode || viewMode) ? (
          tagList.map((tag, index) => (
            <TagListItem
              key={tag.id || index}
              tag={tag}
              hasBorder={index !== tagList.length - 1}
              handleCheckboxChange={handleCheckboxChange}
              attachTagsMode={attachTagsMode}
              detachTagsMode={detachTagsMode}
              viewMode={viewMode}
            />
          ))
        ) : (
          <>
            {tagList
              .filter((tag) => tag.isDefaultTag === true)
              .map((tag, index, arr) => (
                <TagListItem
                  key={tag.id || index}
                  tag={tag}
                  hasBorder={
                    index !== arr.length - 1 ||
                    tagList.some((t) => t.isDefaultTag === false)
                  }
                  handleCheckboxChange={handleCheckboxChange}
                  attachTagsMode={attachTagsMode}
                  detachTagsMode={detachTagsMode}
                  viewMode={viewMode}
                />
              ))}
            {tagList
              .filter((tag) => tag.isDefaultTag === false)
              .map((tag, index, arr) => (
                <TagListItem
                  key={tag.id || index}
                  tag={tag}
                  hasBorder={index !== arr.length - 1}
                  handleCheckboxChange={handleCheckboxChange}
                  attachTagsMode={attachTagsMode}
                  detachTagsMode={detachTagsMode}
                  viewMode={viewMode}
                />
              ))}
          </>
        )}
        {hasNextPage && (
          <Box
            ref={ref}
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding="1rem"
          >
            <CircularProgress
              size={"1.5rem"}
              sx={{
                color: colors.primary.main,
              }}
            />
          </Box>
        )}
      </Box>
    );
  }
  return null; // Return null or a fallback if no conditions are met
};

export default TagList;
